import React, { useState, useCallback, useMemo, useEffect } from 'react';
import styled from '@emotion/styled';
import { useDispatch } from 'react-redux';
import { omit } from 'lodash';
import {
  setDraftAvoidAreas,
  clearDraftAvoidAreas,
  setDraftAvoidSegments,
  clearDraftAvoidSegments,
} from 'actions';
import { getSafeValue } from '../../../utils/security';
import { sanitizeFleetIdentifier } from '../../../utils/FleetHelpers';
import { generateAvoidAreasFromFileProblem } from '../../../utils/territories/TerritoriesFromJson';
import { parseAvoidSegment } from '../../../utils/avoidAreaHelpers';
import { colors, fontSize } from '../../../global/variables';
import infoIcon from '../../../global/img/information_.svg';
import Checkbox from '../../Form/Checkbox';
import EVSelector from './EVSelector';
import AvoidAreasEditor from './AvoidAreasEditor';
import AvoidZonesEditor from './AvoidZonesEditor';
import AvoidExcludeEditor from './AvoidExcludeEditor';
import TrafficRoutingEditor from './TrafficRoutingEditor';
import TruckRoadTypesEditor from './TruckRoadTypesEditor';
import {
  StyledEditor,
  StyledEditorBody,
  StyledGrid,
  StyledField,
  StyledLabel,
  StyledRequiredMark,
  StyledInput,
  StyledSelect,
  StyledFooter,
  StyledCancelButton,
  StyledSaveButton,
} from './FleetEditorStyled';

const { aqua, secondaryTextColor } = colors;
const { smaller, small } = fontSize;

const AREA_AVOID_KEYS = ['areas', 'features', 'segments'];
const ZONE_AVOID_KEYS = ['zoneCategories', 'zoneIdentifiers'];
const TRUCK_ROAD_TYPES_AVOID_KEYS = ['truckRoadTypes'];

const StyledEvToggleRow = styled.div({
  display: 'flex',
  alignItems: 'center',
  marginBottom: '0.9rem',
});

const StyledEVSectionTitle = styled.div({
  fontSize: smaller,
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  fontWeight: 700,
  color: aqua,
  marginBottom: '0.5rem',
});

const StyledInfoIcon = styled.img({
  width: '1rem',
  height: '1rem',
  marginLeft: '0.3rem',
  cursor: 'help',
  opacity: 0.7,
  verticalAlign: 'middle',
});

const StyledEvAuthNotification = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: '0.8rem',
  padding: '0.6rem 1rem',
  marginBottom: '0.9rem',
  borderRadius: '0.12rem',
  backgroundColor: 'rgba(255, 152, 0, 0.08)',
  border: '1px solid rgba(255, 152, 0, 0.4)',
  fontSize: small,
  color: secondaryTextColor,
  a: {
    fontSize: small,
    color: aqua,
    textDecoration: 'none',
    whiteSpace: 'nowrap',
    fontWeight: 700,
    ':hover': {
      textDecoration: 'underline',
    },
  },
});

const FleetProfileEditor = ({
  isNew,
  profileIndex,
  profileName,
  fleetType,
  isElectricVehicle,
  evProfile,
  avoid,
  exclude,
  traffic,
  departureTime,
  ignoreRouteViolations,
  oAuth,
  apiKey,
  vehicleAmount,
  fleetTypeOptions,
  canFleetTypeBeElectric,
  showEvInfo,
  onSave,
  onCancel,
  onDraftChange,
  labels: {
    profileNameLabel,
    profileTypeLabel,
    electricVehicleLabel,
    electricVehicleInfoTooltip,
    evAuthErrorMessage,
    evAuthErrorContactUs,
    evContactLink,
    electricVehicleProfileSectionTitle,
    saveProfileLabel,
    cancelProfileLabel,
    profileNamePlaceholder,
    avoidAreasRoadsTitle,
    avoidAreasSectionTitle,
    avoidRoadsSectionTitle,
    drawOnMapLabel,
    avoidAreaBboxTab,
    avoidAreaPolygonTab,
    avoidAreaNorthLabel,
    avoidAreaSouthLabel,
    avoidAreaEastLabel,
    avoidAreaWestLabel,
    avoidAreaPolygonPlaceholder,
    avoidAreaApplyLabel,
    avoidAreaCancelLabel,
    avoidAreaInvalidError,
    avoidAreaEmptyLabel,
    avoidAreaRemoveTooltip,
    avoidAreaManualEntryToggleLabel,
    avoidFeatures,
    avoidTruckRoadTypesLabels,
    avoidSegmentsSectionTitle,
    avoidSegmentPickOnMapLabel,
    avoidSegmentEmptyLabel,
    avoidSegmentRemoveTooltip,
    avoidSegmentNoPreviewTooltip,
    avoidZonesTitle,
    avoidZoneCategoriesTitle,
    avoidZoneCategoriesLabels,
    avoidZoneExceptLabel,
    avoidZoneIdPlaceholder,
    avoidZoneAddLabel,
    avoidZoneRemoveTooltip,
    avoidSpecificZoneIdsTitle,
    avoidZoneIdentifiersEmptyLabel,
    avoidZoneNoPreviewNote,
    excludeTitle,
    excludeHint,
    excludeCountriesTitle,
    excludeCountryPlaceholder,
    excludeAddLabel,
    excludeCountryRemoveTooltip,
    excludeCountriesEmptyLabel,
    excludeNoPreviewNote,
    trafficRoutingTitle,
    trafficRoutingHint,
    trafficModeLabel,
    trafficModeLabels,
    departureTimeLabel,
    ignoreRouteViolationsLabel,
    ignoreRouteViolationsLabels,
    truckRoadTypesTitle,
    truckRoadTypesHint,
  },
}) => {
  const [draftName, setDraftName] = useState(isNew ? '' : profileName);
  const [draftFleetType, setDraftFleetType] = useState(isNew ? 'car' : fleetType);
  const [draftIsElectricVehicle, setDraftIsElectricVehicle] = useState(
    isNew ? false : isElectricVehicle,
  );
  const [draftEvProfile, setDraftEvProfile] = useState(isNew ? null : evProfile);
  const [draftAvoid, setDraftAvoid] = useState(isNew ? null : avoid);
  const [draftExclude, setDraftExclude] = useState(isNew ? null : exclude);
  const [draftTrafficRouting, setDraftTrafficRouting] = useState(
    isNew ? null : { traffic, departureTime, ignoreRouteViolations },
  );
  const [evAuthError, setEvAuthError] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    if (onDraftChange) onDraftChange({ name: draftName, fleetType: draftFleetType });
  }, [draftName, draftFleetType, onDraftChange]);

  useEffect(() => {
    const areas = draftAvoid?.areas;
    if (!areas || areas.length === 0) {
      dispatch(clearDraftAvoidAreas());
      return;
    }
    const mapAreas =
      generateAvoidAreasFromFileProblem({ fleet: { profiles: [{ avoid: { areas } }] } }, []) || [];
    dispatch(setDraftAvoidAreas(profileIndex, mapAreas));
  }, [draftAvoid?.areas, profileIndex, dispatch]);

  useEffect(() => () => dispatch(clearDraftAvoidAreas()), [dispatch]);

  useEffect(() => {
    const segments = draftAvoid?.segments;
    if (!segments || segments.length === 0) {
      dispatch(clearDraftAvoidSegments());
      return;
    }
    const segmentIds = segments.map((s) => parseAvoidSegment(s)?.id).filter(Boolean);
    dispatch(setDraftAvoidSegments(profileIndex, segmentIds));
  }, [draftAvoid?.segments, profileIndex, dispatch]);

  useEffect(() => () => dispatch(clearDraftAvoidSegments()), [dispatch]);

  const handleEVAuthError = useCallback(() => {
    setDraftIsElectricVehicle(false);
    setEvAuthError(true);
  }, []);

  useEffect(() => {
    if (onDraftChange) onDraftChange({ name: draftName, fleetType: draftFleetType });
  }, [draftName, draftFleetType, onDraftChange]);

  useEffect(() => {
    if (onDraftChange) onDraftChange({ name: draftName, fleetType: draftFleetType });
  }, [draftName, draftFleetType, onDraftChange]);

  const onNameChange = useCallback(
    (e) => setDraftName(sanitizeFleetIdentifier(e.target.value)),
    [],
  );

  const onFleetTypeChange = useCallback(
    (e) => {
      const newFleetType = e.target.value;
      setDraftFleetType(newFleetType);
      if (draftIsElectricVehicle && !canFleetTypeBeElectric(newFleetType))
        setDraftIsElectricVehicle(false);
    },
    [draftIsElectricVehicle, canFleetTypeBeElectric],
  );

  const onElectricVehicleToggle = useCallback((checked) => {
    setDraftIsElectricVehicle(checked);
    if (checked) setEvAuthError(false);
  }, []);

  const onEVProfileChange = useCallback(
    (data) =>
      setDraftEvProfile((prev) => {
        if (!data) return null;
        if (data.evMode === 'maxDistance') {
          return {
            evMode: 'maxDistance',
            maxDistance: data.maxDistance,
            ...(data.profileId && {
              profileId: data.profileId,
              make: data.make,
              model: data.model,
              variant: data.variant,
            }),
          };
        }
        return { ...data, charging: prev?.charging };
      }),
    [],
  );

  const onChargingChange = useCallback(
    (chargingData) => setDraftEvProfile((prev) => ({ ...prev, charging: chargingData })),
    [],
  );

  const onAreasAvoidChange = useCallback(
    (updatedAreasAvoid) =>
      setDraftAvoid((prev) => {
        const merged = { ...omit(prev, AREA_AVOID_KEYS), ...updatedAreasAvoid };
        return Object.keys(merged).length ? merged : null;
      }),
    [],
  );

  const onZonesAvoidChange = useCallback(
    (updatedZonesAvoid) =>
      setDraftAvoid((prev) => {
        const merged = { ...omit(prev, ZONE_AVOID_KEYS), ...updatedZonesAvoid };
        return Object.keys(merged).length ? merged : null;
      }),
    [],
  );

  const onTruckRoadTypesAvoidChange = useCallback(
    (updatedTruckRoadTypesAvoid) =>
      setDraftAvoid((prev) => {
        const merged = {
          ...omit(prev, TRUCK_ROAD_TYPES_AVOID_KEYS),
          ...updatedTruckRoadTypesAvoid,
        };
        return Object.keys(merged).length ? merged : null;
      }),
    [],
  );

  const onExcludeChange = useCallback((updatedExclude) => setDraftExclude(updatedExclude), []);

  const onTrafficRoutingChange = useCallback(
    (updatedTrafficRouting) => setDraftTrafficRouting(updatedTrafficRouting),
    [],
  );

  const isValid = useMemo(
    () => (draftName || '').trim().length > 0 && !!draftFleetType,
    [draftName, draftFleetType],
  );

  const onSaveClick = useCallback(() => {
    onSave({
      name: draftName,
      fleetType: draftFleetType,
      isElectricVehicle: draftIsElectricVehicle,
      evProfile: draftIsElectricVehicle ? draftEvProfile : null,
      avoid: draftAvoid,
      exclude: draftExclude,
      traffic: draftTrafficRouting?.traffic || null,
      departureTime: draftTrafficRouting?.departureTime || null,
      ignoreRouteViolations: draftTrafficRouting?.ignoreRouteViolations || null,
    });
  }, [
    onSave,
    draftName,
    draftFleetType,
    draftIsElectricVehicle,
    draftEvProfile,
    draftAvoid,
    draftExclude,
    draftTrafficRouting,
  ]);

  return (
    <StyledEditor>
      <StyledEditorBody>
        <StyledGrid>
          <StyledField>
            <StyledLabel htmlFor="input-fleet-profile-name">
              {profileNameLabel}
              <StyledRequiredMark>*</StyledRequiredMark>
            </StyledLabel>
            <StyledInput
              id="input-fleet-profile-name"
              type="text"
              placeholder={profileNamePlaceholder}
              value={draftName}
              onChange={onNameChange}
            />
          </StyledField>
          <StyledField>
            <StyledLabel htmlFor="input-fleet-profile-type">
              {profileTypeLabel}
              <StyledRequiredMark>*</StyledRequiredMark>
            </StyledLabel>
            <StyledSelect
              id="input-fleet-profile-type"
              value={draftFleetType}
              onChange={onFleetTypeChange}
            >
              {Object.keys(fleetTypeOptions)
                .filter((typeID) => typeID !== 'electricVehicle')
                .map((typeID) => (
                  <option key={typeID} value={typeID}>
                    {getSafeValue(fleetTypeOptions, typeID)}
                  </option>
                ))}
            </StyledSelect>
          </StyledField>
        </StyledGrid>
        {canFleetTypeBeElectric(draftFleetType) && (
          <StyledEvToggleRow>
            <Checkbox
              id="input-fleet-profile-ev"
              value={draftIsElectricVehicle}
              label={electricVehicleLabel}
              onChange={onElectricVehicleToggle}
            />
            {showEvInfo && (
              <StyledInfoIcon src={infoIcon} alt="info" title={electricVehicleInfoTooltip} />
            )}
          </StyledEvToggleRow>
        )}
        {evAuthError && (
          <StyledEvAuthNotification>
            <span>{evAuthErrorMessage}</span>
            <a href={evContactLink} target="_blank" rel="noopener noreferrer">
              {evAuthErrorContactUs}
            </a>
          </StyledEvAuthNotification>
        )}
        {draftIsElectricVehicle && (
          <>
            <StyledEVSectionTitle>{electricVehicleProfileSectionTitle}</StyledEVSectionTitle>
            <EVSelector
              oAuth={oAuth}
              apiKey={apiKey}
              onEVProfileChange={onEVProfileChange}
              onAuthError={handleEVAuthError}
              initialProfile={draftEvProfile}
              charging={draftEvProfile?.charging}
              vehicleAmount={vehicleAmount}
              onChargingChange={onChargingChange}
            />
          </>
        )}
        <AvoidAreasEditor
          key={draftFleetType}
          fleetType={draftFleetType}
          avoid={draftAvoid}
          onChange={onAreasAvoidChange}
          labels={{
            avoidAreasRoadsTitle,
            avoidAreasSectionTitle,
            avoidRoadsSectionTitle,
            drawOnMapLabel,
            avoidAreaBboxTab,
            avoidAreaPolygonTab,
            avoidAreaNorthLabel,
            avoidAreaSouthLabel,
            avoidAreaEastLabel,
            avoidAreaWestLabel,
            avoidAreaPolygonPlaceholder,
            avoidAreaApplyLabel,
            avoidAreaCancelLabel,
            avoidAreaInvalidError,
            avoidAreaEmptyLabel,
            avoidAreaRemoveTooltip,
            avoidAreaManualEntryToggleLabel,
            avoidFeatures,
            avoidSegmentsSectionTitle,
            avoidSegmentPickOnMapLabel,
            avoidSegmentEmptyLabel,
            avoidSegmentRemoveTooltip,
            avoidSegmentNoPreviewTooltip,
          }}
        />
        <AvoidZonesEditor
          avoid={draftAvoid}
          onChange={onZonesAvoidChange}
          labels={{
            avoidZonesTitle,
            avoidZoneCategoriesTitle,
            avoidZoneCategoriesLabels,
            avoidZoneExceptLabel,
            avoidZoneIdPlaceholder,
            avoidZoneAddLabel,
            avoidZoneRemoveTooltip,
            avoidSpecificZoneIdsTitle,
            avoidZoneIdentifiersEmptyLabel,
            avoidZoneNoPreviewNote,
          }}
        />
        <AvoidExcludeEditor
          exclude={draftExclude}
          onChange={onExcludeChange}
          labels={{
            excludeTitle,
            excludeHint,
            excludeCountriesTitle,
            excludeCountryPlaceholder,
            excludeAddLabel,
            excludeCountryRemoveTooltip,
            excludeCountriesEmptyLabel,
            excludeNoPreviewNote,
          }}
        />
        <TrafficRoutingEditor
          traffic={draftTrafficRouting?.traffic}
          departureTime={draftTrafficRouting?.departureTime}
          ignoreRouteViolations={draftTrafficRouting?.ignoreRouteViolations}
          onChange={onTrafficRoutingChange}
          labels={{
            trafficRoutingTitle,
            trafficRoutingHint,
            trafficModeLabel,
            trafficModeLabels,
            departureTimeLabel,
            ignoreRouteViolationsLabel,
            ignoreRouteViolationsLabels,
          }}
        />
        {draftFleetType === 'truck' && (
          <TruckRoadTypesEditor
            truckRoadTypes={draftAvoid?.truckRoadTypes}
            onChange={onTruckRoadTypesAvoidChange}
            labels={{
              truckRoadTypesTitle,
              truckRoadTypesHint,
              avoidTruckRoadTypesLabels,
            }}
          />
        )}
      </StyledEditorBody>
      <StyledFooter>
        <StyledCancelButton id="button-fleet-profile-cancel" type="button" onClick={onCancel}>
          {cancelProfileLabel}
        </StyledCancelButton>
        <StyledSaveButton
          id="button-fleet-profile-save"
          type="button"
          disabled={!isValid}
          onClick={onSaveClick}
        >
          {saveProfileLabel}
        </StyledSaveButton>
      </StyledFooter>
    </StyledEditor>
  );
};

export default FleetProfileEditor;
